import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {BehaviorSubject, combineLatest, forkJoin, merge, Observable, of, ReplaySubject, Subject} from 'rxjs';
import {Router} from '@angular/router';
import {CUSTOM_DATE_FORMATS} from '../economics-aggregated/ec-economics-aggregated.component';
import * as moment from 'moment-timezone';
import {Moment} from 'moment-timezone';
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    filter,
    first,
    map,
    shareReplay,
    startWith,
    switchMap,
    takeUntil,
    tap
} from 'rxjs/operators';
import {ECMember, ECMemberParticipationType, EnergyCommunity, EnergyCommunityNamedPod, EntityType} from 'smag-commons/cfg/hierarchy';
import {HierarchyEntity, HierarchyService} from '../../../shared/hierarchy.service';
import {SmagApiService} from '../../../api/smag-api.service';
import {DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE} from '@angular/material/core';
import {MAT_MOMENT_DATE_ADAPTER_OPTIONS, MomentDateAdapter} from '@angular/material-moment-adapter';
import {
    ECCacvRedistributionMode,
    ECEconomicsPlantsPod,
    ECEconomicsPlantsPods,
    ECEconomicsPlantsResponse,
    EconomicsErrorType,
    ECPlantTip,
    ECPlantType
} from 'smag-commons/dto/eco';
import {I18NService} from '../../../i18n/i18n.service';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {I18NMatPaginatorIntl, I18NMatPaginatorIntlConfig} from '../../../shared/i18n-mat-paginator-intl';
import {MatPaginator, MatPaginatorIntl} from '@angular/material/paginator';
import {MsgBoxService} from '../../../shared/msg-box.service';
import {DialogService} from '../../../shared/dialog/dialog.service';
import {ConfigurationService} from '../../../shared/configuration.service';
import {getSum, isDefined} from '../../../commons/commons';
import {ECCacvRedistribution, ECEconomicsPlant, ECSetPodsYearlyValueRequest, REIRedistributionStatus} from 'smag-commons/dto/eco/community';
import {can} from '../../../shared/permissions';
import {Permission, Scope} from 'smag-commons/dto/api';
import {hasError, showError} from '../../../shared/economics-errors';
import {mkMode} from '../../../shared/energy-community';
import {BillingPeriod, BillingPeriodConfig} from '../billing-filter/ec-billing-filter.component';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {CONTENT_DIALOG, DATE_SELECTION} from '../economics-aggregated/ec-select-date/ec-select-date.component';
import {CACV_MODE, EC_PLANTS_REI_REDISTRIBUTION, PLANT_TIP_QUOTE} from 'smag-commons/cfg/economics-values';
import {ReiResponse} from './set-rei/set-rei.component';
import {TipResponse} from './set-tip/set-tip.component';
import {AuthService} from 'auth-commons';
import {
    CACV_STRING,
    DataSourceInfo,
    DEFAULT_PAGE_SIZE,
    DEFAULT_PAGE_SIZES,
    generatesTip,
    getDataSourceIconInfo,
    getMomentFromMs,
    getPastMonth,
    isWholeMonthSelected,
    isWholeYearSelected,
    missingCacvMode,
    missingCosts,
    missingReiRedistribution,
    missingTipQuote,
    mkBillingPeriodParams,
    mkConsolidatedMonths,
    mkYearlyValue,
    sanityFromTimeActivationDate,
    sanityToTime,
    YearSummaryConsolidation
} from '../../../shared/ec-economics';
import {CacvYearlyValue} from './set-cacv/set-cacv.component';
import {YearlyValue} from 'smag-commons/cfg/value/yearly-value';
import {ComputationStatusService} from '../../../shared/compute-status.service';

interface Plant extends ECEconomicsPlant {
    entity?: HierarchyEntity;
}

export interface PlantsPod extends ECEconomicsPlantsPod {
    entity?: HierarchyEntity;
    member?: ECMember;
    plants: Plant[];
    generatesTip: boolean;
    cacvMode: ECCacvRedistributionMode;
}

interface PlantsPods extends ECEconomicsPlantsPods {
    pods: PlantsPod[];
}

interface PlantsType {
    name: string;
    value?: ECPlantType;
}

interface FormValues {
    searchText: string;
    plantType: ECPlantType;
}

enum DialogMode {
    CACV = 'cacv',
    CACV_PLANTS = 'cacv_plants',
    CACV_REDISTRIBUTION = 'cacv_redistribution',
    CACV_SET_ASIDE = 'cacv_set_aside',
    CACV_AS_TIP = 'cacv_as_tip',
    TIP = 'tip',
    REI = 'rei'
}

const USE_FILTER = true;
const NULL_DATA = '€ -';

@Component({
    selector: 'app-ec-plants',
    templateUrl: './ec-plants.component.html',
    styleUrls: ['./ec-plants.component.scss'],
    animations: [
        trigger('detailExpand', [
            state('collapsed', style({height: '0px', minHeight: '0'})),
            state('expanded', style({height: '*'})),
            transition('expanded <=> collapsed', animate('225ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
        ]),
    ],
    providers: [
        {
            provide: DateAdapter,
            useClass: MomentDateAdapter,
            deps: [MAT_DATE_LOCALE, MAT_MOMENT_DATE_ADAPTER_OPTIONS],
        },
        {provide: MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS},
        {
            provide: I18NMatPaginatorIntlConfig,
            useValue: {itemsPerPageLabel: 'Plants per page'} as I18NMatPaginatorIntlConfig
        },
        {provide: MatPaginatorIntl, useClass: I18NMatPaginatorIntl, deps: [I18NService, I18NMatPaginatorIntlConfig]}
    ]
})
export class ECPlantsComponent implements OnInit, AfterViewInit, OnDestroy {
    private destroyed = new Subject<void>();
    private reloadPlants: Subject<void> = new Subject<void>();
    private reloadPlantsData = new Subject<void>();
    private reloadPodsData = new Subject<void>();
    private configSubj: BehaviorSubject<void> = new BehaviorSubject<void>(null);
    private currentMassiveReq: ECSetPodsYearlyValueRequest = null;
    private billingPeriodSubj: ReplaySubject<BillingPeriod> = new ReplaySubject<BillingPeriod>(1);
    private formValueSubj: ReplaySubject<FormValues> = new ReplaySubject<FormValues>(1);

    @ViewChild('paginator') private paginator: MatPaginator;
    @ViewChild('configureCacvMode') private configureCacvModeDialog: TemplateRef<any>;
    @ViewChild('configureTipQuote') private configureTipQuoteDialog: TemplateRef<any>;
    @ViewChild('configureRei') private configureReiDialog: TemplateRef<any>;
    @ViewChild('selectDate') private selectDateDialog: TemplateRef<any>;
    @ViewChild('adviceDialog') private adviceDialog: TemplateRef<any>;

    hierarchyEntity: Observable<HierarchyEntity> = null;
    entity: HierarchyEntity = null;
    timeZone: string = null;
    billingPeriodCfg: BillingPeriodConfig = null;
    billingPeriod: BillingPeriod = {} as BillingPeriod;
    singleDate: Moment = null;
    response: ECEconomicsPlantsResponse = null;
    displayedColumns: string[] = ['chevron', 'name', 'type', 'member', 'pod', 'params'];
    plants: BehaviorSubject<PlantsPods> = new BehaviorSubject<PlantsPods>(null);
    expandedPlant: BehaviorSubject<ECEconomicsPlantsPod> = new BehaviorSubject<ECEconomicsPlantsPod>(null);
    allPlantsTypes: PlantsType[] = [
        {name: 'All plants'},
        {name: 'Members plants', value: ECPlantType.MEMBER},
        {name: 'Communities plants', value: ECPlantType.COMMUNITY},
        {name: 'Third party producers plants', value: ECPlantType.THIRD_PARTY}
    ];
    form: FormGroup;
    summaryResponse: YearSummaryConsolidation[];
    isWholeYear = false;
    isSingleMonth: boolean;
    selectedYear: number;
    isRepresentative: boolean;
    dataSourceInfo: DataSourceInfo;
    showComputationWarning = false;
    plantsTipOverMaximum = false;

    // exported in HTML
    readonly DEFAULT_PAGE_SIZE = DEFAULT_PAGE_SIZE;
    readonly DEFAULT_PAGE_SIZES = DEFAULT_PAGE_SIZES;
    readonly EconomicsErrorType = EconomicsErrorType;
    readonly USE_FILTER = USE_FILTER;
    readonly isDefined = isDefined;
    readonly NULL_DATA = NULL_DATA;
    readonly hasError = hasError;
    readonly showError = showError;
    readonly REIRedistributionStatus = REIRedistributionStatus;
    readonly getMomentFromMs = getMomentFromMs;
    readonly getPastMonth = getPastMonth;
    readonly CONTENT_DIALOG = CONTENT_DIALOG;
    readonly DATE_SELECTION = DATE_SELECTION;
    readonly getSum = getSum;
    readonly missingCacvMode = missingCacvMode;
    readonly missingTipQuote = missingTipQuote;
    readonly missingCosts = missingCosts;
    readonly missingReiRedistribution = missingReiRedistribution;
    readonly DialogMode = DialogMode;
    readonly CACV_STRING = CACV_STRING;
    readonly ECCacvRedistributionMode = ECCacvRedistributionMode;

    constructor(private readonly hierarchyService: HierarchyService,
                private readonly apiService: SmagApiService,
                private readonly fb: FormBuilder,
                private readonly i18n: I18NService,
                private readonly msgBoxService: MsgBoxService,
                private readonly dialogService: DialogService,
                private readonly configurationService: ConfigurationService,
                private readonly router: Router,
                private readonly auth: AuthService,
                private readonly computationService: ComputationStatusService) {
        this.form = this.fb.group({
            searchText: new FormControl('', []),
            plantType: new FormControl(this.allPlantsTypes[0].value, [])
        });
    }


    ngOnInit() {
        this.isRepresentative = this.auth.profile.scopes?.includes(Scope.REPRESENTATIVE);
        this.configurationService.timeConfig
            .pipe(first())
            .subscribe(timeCfg => this.timeZone = timeCfg.time_zone);

        this.computationService.showComputingWarning
            .pipe(takeUntil(this.destroyed))
            .subscribe(bool => this.showComputationWarning = bool);

        // NOTE: as we store time range in URL, we need to avoid "navigation" to same entity when
        //       setting URL parameters for time-range
        this.hierarchyEntity = this.hierarchyService.selection.pipe(
            distinctUntilChanged((h1, h2) => h1?.id === h2?.id),
            shareReplay(1),
            takeUntil(this.destroyed)
        );

        this.hierarchyEntity.pipe(
            filter(he => !!he),
            takeUntil(this.destroyed)
        ).subscribe(he => {
            this.entity = he;
            if (can(he.permissions, Permission.READ)) {
                this.loadECMode(he.id);
            }
        });

        this.form.valueChanges.pipe(
            startWith(this.form.value),
            takeUntil(this.destroyed),
            debounceTime(500))
            .subscribe((value) => {
                this.formValueSubj.next(value);
            });

        this.hierarchyEntity.pipe(
            filter(hierarchyEntity => !!hierarchyEntity),
            switchMap(hierarchyEntity => can(hierarchyEntity.permissions, Permission.READ) ?
                this.apiService.getEntity(hierarchyEntity.id) :
                of(null)),
            takeUntil(this.destroyed)
        ).subscribe(entity => {
            this.billingPeriodCfg = {
                pastDate: entity ? (entity as EnergyCommunity).activation_date || null : null,
                futureDate: null
            };
        });

        combineLatest([this.hierarchyEntity, this.billingPeriodSubj, this.configurationService.timeConfig, this.configSubj])
            .pipe(takeUntil(this.destroyed))
            .subscribe(([hierarchyEntity, billingPeriod, timeCfg]) => {
                const timeZone = timeCfg.time_zone;
                this.mkData(hierarchyEntity, billingPeriod.fromTime, billingPeriod.toTime);
                this.singleDate = moment.tz(billingPeriod.fromTime, timeZone);
            });
    }

    ngAfterViewInit() {
        merge(this.paginator.page,
            this.reloadPlants.pipe(tap(() => this.paginator.firstPage()))).pipe(
            startWith(undefined as unknown),
            switchMap(() => combineLatest([this.hierarchyEntity, this.billingPeriodSubj, this.formValueSubj, this.configSubj])),
            switchMap(([hierarchyEntity, billingPeriod, formValues]) => {
                if (this.isDetailViewable(hierarchyEntity)) {
                    this.plants.next(null); // triggers spinner
                    return this.mkPlants(hierarchyEntity, billingPeriod.fromTime, billingPeriod.toTime, this.paginator.pageIndex + 1, this.paginator.pageSize, formValues.searchText, formValues.plantType)
                        .pipe(startWith(null as PlantsPods), // triggers spinner
                            catchError(err => {
                                this.msgBoxService.error(this.i18n.translate('An error occurred while retrieving plants'), err);
                                return of(null as PlantsPods);
                            }));
                } else {
                    return of(null);
                }
            }),
            takeUntil(this.destroyed)
        ).subscribe((plants) => {
            if (plants) {
                this.plants.next(plants);
                this.expandedPlant.next(null);
            }
        });
    }

    ngOnDestroy() {
        this.destroyed.next();
        this.destroyed.complete();
    }

    get searchText() {
        return this.form.controls.searchText.value as string;
    }

    get hasNoEcPlants() {
        return !isDefined(this.response.ec_plants_count) || this.response.ec_plants_count === 0;
    }

    get hasNoMemberPlants() {
        return !isDefined(this.response.member_plants_count) || this.response.member_plants_count === 0;
    }

    get hasNoThirdPlants() {
        return !isDefined(this.response.third_party_plants_count) || this.response.third_party_plants_count === 0;
    }

    get onlyCommunityPlants() {
        return !this.hasNoEcPlants && this.hasNoMemberPlants && this.hasNoThirdPlants;
    }

    onClickClearSearchText() {
        this.form.controls.searchText.setValue('');
    }

    onChangeBillingPeriod(period: BillingPeriod) {
        this.billingPeriodSubj.next(period);
        this.billingPeriod = period;
        this.isWholeYear = isWholeYearSelected(period.fromTime, period.toTime, this.timeZone, period.type);
        this.isSingleMonth = isWholeMonthSelected(period.fromTime, period.toTime, this.timeZone, period.type);
        this.selectedYear = moment.tz(period.fromTime, this.timeZone).year();
        this.router.navigate([],
            {
                queryParams: mkBillingPeriodParams(period),
                queryParamsHandling: 'merge'
            }).then();
    }

    onSelectPlant(plant: ECEconomicsPlantsPod) {
        this.expandedPlant.next(this.expandedPlant.value === plant ? null : plant);
    }

    onClickGseDataEntered() {
        this.reloadPlants.next();
        this.billingPeriodSubj.next(this.billingPeriod);
    }

    goBack() {
        this.router.navigate(['/c', 'ec', 'economics', 'aggregated'],
            {queryParamsHandling: 'merge'}).then();
    }

    isDetailViewable(entity: HierarchyEntity): boolean {
        return !!entity && can(entity.permissions, Permission.READ);
    }

    setType(plantsPod: PlantsPod) {
        if (plantsPod.member) {
            if (plantsPod.member.participation_type === ECMemberParticipationType.THIRD_PARTY_PRODUCER) {
                return ECMemberParticipationType.THIRD_PARTY_PRODUCER;
            }
        }
        if (plantsPod.producer) {
            return 'Producer';
        } else {
            return 'Prosumer';
        }
    }

    isCommunityPlant(plant: PlantsPod | ECEconomicsPlantsPod) {
        return !plant.subject_id;
    }

    getYear(ms: number) {
        return moment.tz(ms, this.timeZone).startOf('year').valueOf();
    }

    exportEconomicsToExcel() {
        // TODO if logged user is representative, the exported excel will not contains rei of member or third plants
        /*        this.templateIsDownloading = true;
                this.apiService.exportEconomicsToExcel(ECEconomicsTemplateType.RECAP,
                    this.templateEntity.id,
                    this.templatePeriod.fromTime,
                    this.templatePeriod.toTime,
                    this.entityName)
                    .subscribe(([data, filename]) => {
                        downloadBlob(data, filename);
                        this.templateIsDownloading = false;
                        this.msgBoxService.info(this.i18n.translate('File downloaded!'));
                    }, error => {
                        this.templateIsDownloading = false;
                        this.msgBoxService.error(this.i18n.translate('An error occurred while fetching the template'));
                    });*/
    }

    openConfigureDialog(mode: DialogMode) {
        if (this.isWholeYear) {
            this.openDialog(mode);
        } else {
            this.dialogService.open<any, Moment>({
                title: this.i18n.translate('Select an annual period to configure'),
                template: this.selectDateDialog,
                context: {}
            }).closed.then(data => {
                if (data) {
                    this.selectedYear = data.year();
                    this.openDialog(mode);
                }
            });
        }
    }

    private openMassiveDialog(mode: DialogMode) {
        const title = {
            [DialogMode.CACV_PLANTS]: 'Do you want to allocate CACV to plants?',
            [DialogMode.CACV_REDISTRIBUTION]: 'Do you want to allocate CACV to consumer utilities?',
            [DialogMode.CACV_SET_ASIDE]: 'Do you want to exclude CACV from redistribution?',
            [DialogMode.CACV_AS_TIP]: 'Do you want to allocate CACV in the same way as TIP?',
            [DialogMode.TIP]: 'Do you want to enter this TIP for all subject plants?',
            [DialogMode.REI]: 'Do you want to make the choice for all community facilities?',
        }[mode];

        this.dialogService.open<any, boolean>({
            title: this.i18n.translate(title),
            template: this.adviceDialog,
            context: {
                mode
            }
        }).closed.then(data => {
            if (data && isDefined(this.currentMassiveReq)) {
                if (mode === DialogMode.CACV_PLANTS || mode === DialogMode.CACV_REDISTRIBUTION || mode === DialogMode.CACV_SET_ASIDE || mode === DialogMode.CACV_AS_TIP) {
                    this.setYearlyValue(mkYearlyValue(this.currentMassiveReq.name, this.currentMassiveReq.value,
                        this.entity.id, this.currentMassiveReq.year));
                } else {
                    this.setConfig(this.currentMassiveReq);
                }
            }
        });
    }

    private openTipDialog(mode: DialogMode) {
        this.dialogService.open<any, TipResponse>({
            title: this.i18n.translate('Massively configure the share of the premium rate due to the facilities'),
            template: this.configureTipQuoteDialog,
            context: {}
        }).closed.then(data => {
            if (isDefined(data) &&
                isDefined(data.plant_tip) &&
                isDefined(data.year) &&
                isDefined(data.mode) &&
                isDefined(data.type)) {
                this.currentMassiveReq = {
                    name: PLANT_TIP_QUOTE,
                    value: {
                        plant_tip: data.plant_tip,
                        mode: data.mode,
                        type: data.type,
                    } as ECPlantTip,
                    year: data.year
                };
                this.openMassiveDialog(mode);
            }
        });
    }

    private openCacvDialog() {
        this.dialogService.open<any, CacvYearlyValue>({
            title: this.i18n.translate('Configure the destination of CACV'),
            template: this.configureCacvModeDialog,
            context: {}
        }).closed.then(data => {
            if (isDefined(data) && isDefined(data.cacvMode) && isDefined(data.year)) {
                this.currentMassiveReq = {
                    name: CACV_MODE,
                    value: {mode: data.cacvMode} as ECCacvRedistribution,
                    year: data.year
                };

                let mode;
                if (data.cacvMode === ECCacvRedistributionMode.PLANTS) {
                    mode = DialogMode.CACV_PLANTS;
                } else if (data.cacvMode === ECCacvRedistributionMode.REDISTRIBUTION) {
                    mode = DialogMode.CACV_REDISTRIBUTION;
                } else if (data.cacvMode === ECCacvRedistributionMode.SET_ASIDE) {
                    mode = DialogMode.CACV_SET_ASIDE;
                } else if (data.cacvMode === ECCacvRedistributionMode.AS_TIP) {
                    mode = DialogMode.CACV_AS_TIP;
                }
                this.openMassiveDialog(mode);
            }
        });
    }

    private openReiDialog(mode: DialogMode) {
        this.dialogService.open<any, ReiResponse>({
            title: this.i18n.translate('Massively configure the revenues of the community facilities resulting from withdrawal of energy input'),
            template: this.configureReiDialog,
            context: {}
        }).closed.then(data => {
            if (isDefined(data) && isDefined(data.useReiVal) && isDefined(data.year)) {
                this.currentMassiveReq = {
                    name: EC_PLANTS_REI_REDISTRIBUTION,
                    value: data.useReiVal ? 1 : 0,
                    year: data.year
                };
                this.openMassiveDialog(mode);
            }
        });
    }

    private openDialog(mode: DialogMode) {
        if (mode === DialogMode.CACV) {
            this.openCacvDialog();
        } else if (mode === DialogMode.TIP) {
            this.openTipDialog(mode);
        } else if (mode === DialogMode.REI) {
            this.openReiDialog(mode);
        }
    }

    private mkData(entity: HierarchyEntity, from: number, to: number) {
        this.response = null;
        if (entity?.type === EntityType.ENERGY_COMMUNITY) {
            const fromTime = sanityFromTimeActivationDate(from, this.billingPeriodCfg.pastDate, this.timeZone);
            const endTime = sanityToTime(this.timeZone, to);
            this.reloadPlantsData.next();
            this.computationService.checkComputationStatus(entity.id, moment.tz(this.billingPeriod.fromTime, this.timeZone).startOf('month').year())
                .pipe(takeUntil(merge(this.destroyed, this.reloadPlantsData)))
                .subscribe(() => {
                    this.apiService.ecEconomicsPlants(entity.id, fromTime, endTime)
                        .subscribe(response => {
                            this.response = response;
                            this.plantsTipOverMaximum = response.surplus_premium_rate.plants_tip_over_maximum;
                        });
                    mkConsolidatedMonths(entity.id, this.billingPeriodCfg.pastDate, this.timeZone, this.apiService)
                        .then(response => this.summaryResponse = response);
                });
        }
    }

    private setConfig(req: ECSetPodsYearlyValueRequest) {
        this.apiService.setPodsYearlyValue(this.entity.id, req).then(() => {
            this.msgBoxService.info(this.i18n.translate('Plants configured correctly'));
            this.configSubj.next();
        }).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while configuring plants'), err));
    }

    private setYearlyValue(value: YearlyValue) {
        this.apiService.setYearlyValue(value)
            .then(() => {
                this.msgBoxService.info(this.i18n.translate('CACV configured correctly'));
                this.configSubj.next();
            }).catch(err => this.msgBoxService.error(this.i18n.translate('An error occurred while configuring CACV'), err));
    }

    private loadECMode(networkId: string) {
        this.apiService.communityPodsStatistics(networkId)
            .pipe(map(stats => mkMode(stats)))
            .subscribe(ecMode => this.dataSourceInfo = getDataSourceIconInfo(ecMode));
    }

    private mkPlants(entity: HierarchyEntity, fromTime: number, toTime: number, page: number, size: number, search?: string, ecPlantType?: ECPlantType): Observable<PlantsPods> {
        if (entity?.type !== EntityType.ENERGY_COMMUNITY) {
            return of({pods: [], total: 0} as PlantsPods);
        }
        const startTime = sanityFromTimeActivationDate(fromTime, this.billingPeriodCfg.pastDate, this.timeZone);
        const endTime = sanityToTime(this.timeZone, toTime);
        this.reloadPodsData.next();
        return this.computationService.checkComputationStatus(entity.id, moment.tz(this.billingPeriod.fromTime, this.timeZone).startOf('month').year())
            .pipe(takeUntil(merge(this.destroyed, this.reloadPodsData)),
                switchMap(() => {
                    return forkJoin([
                        this.apiService.ecEconomicsPlantsPods(entity.id, startTime, endTime, page, size, search, ecPlantType),
                        this.hierarchyService.hierarchyIds.pipe(first())
                    ]).pipe(
                        switchMap(([pods, entities]) => pods.pods.length ?
                            forkJoin(pods.pods.map(pod =>
                                forkJoin([
                                    (pod ? this.apiService.findMemberByPOD(pod.pod) : of(null as ECMember)),
                                    this.apiService.getECPod(entity.id, pod.id)
                                ]).pipe(map(([member, ecPod]) => [pod, member, ecPod] as [ECEconomicsPlantsPod, ECMember, EnergyCommunityNamedPod]))
                            )).pipe(map(podsAndMembersAndEcPods => mkPlantsPods(podsAndMembersAndEcPods, entities, pods))) :
                            of({pods: [], total: 0} as PlantsPods))
                    );
                }));
    }
}

function mkPlantsPods(podsAndMembersAndEcPods: [ECEconomicsPlantsPod, ECMember, EnergyCommunityNamedPod][],
                      entities: { [p: string]: HierarchyEntity },
                      pods: ECEconomicsPlantsPods): PlantsPods {
    return {
        pods: podsAndMembersAndEcPods.map(([pod, member, ecPod]) => mkPlantsPod(pod, member, ecPod, entities, pods.c_acv_mode)),
        total: pods.total,
        errors: pods.errors,
        c_acv_mode: pods.c_acv_mode
    };
}

function mkPlantsPod(pod: ECEconomicsPlantsPod,
                     member: ECMember,
                     ecPod: EnergyCommunityNamedPod,
                     entities: { [entityId: string]: HierarchyEntity },
                     cacvMode: ECCacvRedistributionMode): PlantsPod {
    return {
        ...pod,
        entity: entities[pod.id],
        member,
        plants: pod.plants.map(plant => mkPlant(plant, entities)),
        generatesTip: ecPod.plants.some(p => generatesTip(p.plant)),
        cacvMode
    };
}

function mkPlant(plant: ECEconomicsPlant,
                 entities: { [entityId: string]: HierarchyEntity }): Plant {
    return {
        ...plant,
        entity: entities[plant.id]
    };
}

